/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import translate from "@insite/client-framework/Translate";
import { ButtonIcon } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import { Clickable } from "@insite/mobius/index";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import IconMinus from "@rc/XDL/Icons/IconMinus";
import IconPlus from "@rc/XDL/Icons/IconPlus";
import { buttonTransparent } from "@rc/XDL/PreStyle/button";
import { color } from "@rc/XDL/Utilities";
import React, { ChangeEvent, useState } from "react";
import styled, { css } from "styled-components";

export interface ProductQuantityAddNewItem {
    setQty: any;
    qty: string;
}
type Props = ProductQuantityAddNewItem;

export interface ProductQuantityAddNewItemStyles {
    qtyInput: TextFieldPresentationProps;
}

export const productQuantityAddNewItemStyles: ProductQuantityAddNewItemStyles = {
    qtyInput: {
        cssOverrides: {
            formInputWrapper: css`
                &&&& {
                    input {
                        height: 29px;
                        width: 42px;
                        min-height: 29px;
                        max-height: 29px;
                        border: 1px solid ${color("common.ui5")};
                        border-radius: 2px;
                        background-color: ${color("common.ui1")};
                        text-align: center;
                        padding: 13px 0 13px 0;
                        margin: 0 7px;
                    }
                }

                input[type="number"]::-webkit-outer-spin-button,
                input[type="number"]::-webkit-inner-spin-button {
                    /* ISC-11023 deal with this. */
                    /* stylelint-disable-next-line */
                    -webkit-appearance: none;
                    margin: 0;
                }
                label {
                    display: none;
                }
            `,
            inputSelect: css`
                /* ISC-11023 deal with this. */
                /* stylelint-disable-next-line */
                -moz-appearance: textfield;
                display: none;
            `,
        },
    },
};

const styles = productQuantityAddNewItemStyles;

const navIcon = (
    qtyChangeHandler: Function,
    newQty: number,
    icon: string | React.ComponentType,
    ariaLabel: string,
    disabledFlag: boolean,
    dataTestSelector?: string,
) => (
    <Clickable
        aria-label={ariaLabel}
        disabled={disabledFlag}
        onClick={() => {
            qtyChangeHandler(null as any, newQty);
        }}
        {...(dataTestSelector && { "data-test-selector": dataTestSelector })}
    >
        <VisuallyHidden>{ariaLabel}</VisuallyHidden>
        <ButtonIcon size={10} src={icon} />
    </Clickable>
);

const QtyWrapper = styled.div`
    display: flex;
    align-items: center;
    button {
        padding: 0;
        ${buttonTransparent};
        ${IconWrapper} svg {
            color: ${color("primary")};
        }
    }
`;
const ProductQuantityAddNewItem: React.FC<Props> = ({ qty, setQty }) => {
    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>, newQty?: number) => {
        const value = event ? event.target.value : newQty?.toString() || "";
        if (value.indexOf(".") > -1) {
            return;
        }
        const possibleNumber = Number(value);
        if (!Number.isNaN(possibleNumber)) {
            setQty(value);
        }
    };

    return (
        <QtyWrapper>
            {navIcon(
                qtyChangeHandler,
                Number(qty) - 1,
                IconMinus,
                translate("add"),
                Number(qty) - 1 === 0,
                "pagination_previousButton",
            )}
            <TextField
                type="number"
                min="0"
                max={999999}
                maxLength={6}
                label={translate("QTY")}
                value={qty}
                onChange={qtyChangeHandler}
                data-test-selector="productSelector_qty"
                {...styles.qtyInput}
            />
            {navIcon(
                qtyChangeHandler,
                Number(qty) + 1,
                IconPlus,
                translate("subtract"),
                Number(qty) + 1 > 999999,
                "pagination_previousButton",
            )}
        </QtyWrapper>
    );
};

export default ProductQuantityAddNewItem;
